'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

const Joi = require('@hapi/joi');
/** sets the serialized type */


const SERIALIZED_TYPE = '@tiller/user-library/User';
/**
 * This class is responsible of creating a User.
 *
 * @class User
 */

class User {
  /**
   * Create a new instance of User.
   *
   * @param {number} id - Identifier of the User
   * @param {string} username - Username of the User
   * @param {string} email - Email of the User
   * @param {string} firstName - Firstname of the User
   * @param {string} lastName - lastName of the User
   * @param {Array} storesId - associated stores id.
   * @param {string} freshchatRestoreId - associated stores id.
   *
   * @return {User}
   */
  constructor(id, username, email, firstName, lastName, storesId, config = {}) {
    this.id = id;
    this.username = username;
    this.email = email;
    this.firstName = firstName;
    this.lastName = lastName;
    this.storesId = storesId;
    this.sonarAccess = false;
    this.pieChartAccess = false;
    this.config = config;
  }
  /**
   * Return the user's id
   *
   * @return {number} id
   */


  getId() {
    return this.id;
  }
  /**
   * Return the user's name
   *
   * @return {string} username
   */


  getUsername() {
    return this.username;
  }
  /**
   * Return the user's email
   *
   * @return {string} email
   */


  getEmail() {
    return this.email;
  }
  /**
   * Return the user's firstname.
   *
   * @return {string} firstName
   */


  getFirstName() {
    return this.firstName;
  }
  /**
   * Return the user's lastname.
   *
   * @return {string} lastName
   */


  getLastname() {
    return this.lastName;
  }
  /**
   * Return the user's storesId.
   *
   * @return {number} storesId
   */


  getStoresId() {
    return this.storesId;
  }
  /**
   * Return the user's extra config.
   *
   * @return {object} object containing extra configuration data like freshchat restore ID, etc.
   */


  getConfig() {
    return this.config;
  }
  /**
   * Grant Sonar access
   *
   * @return {User}
   */


  grantSonarAccess() {
    this.sonarAccess = true;
    return this;
  }
  /**
   * Grant Sonar access
   *
   * @return {User}
   */


  revokeSonarAccess() {
    this.sonarAccess = false;
    return this;
  }
  /**
   * Grant Pie chart access
   *
   * @return {User}
   */


  grantPieChartAccess() {
    this.pieChartAccess = true;
    return this;
  }
  /**
   * Grant Pie chart access
   *
   * @return {User}
   */


  revokePieChartAccess() {
    this.pieChartAccess = false;
    return this;
  }
  /**
   * Has user a Sonar access
   *
   * @return {bool}
   */


  hasSonarAccess() {
    return this.sonarAccess == true;
  }
  /**
   * Has user a Pie chart access
   *
   * @return {bool}
   */


  hasPieChartAccess() {
    return this.pieChartAccess == true;
  }
  /**
   * Clone User object (Deep cloning).
   *
   * @return {User} - new instance of User.
   */


  clone() {
    const user = new User(this.id, this.username, this.email, this.firstName, this.lastName, this.storesId, this.config);

    if (this.hasSonarAccess()) {
      user.grantSonarAccess();
    }

    if (this.hasPieChartAccess()) {
      user.grantPieChartAccess();
    }

    return user;
  }
  /**
   * Serializes the User.
   *
   * @return {Object} The serialized object.
   */


  serialize() {
    return {
      serializedType: SERIALIZED_TYPE,
      ...this
    };
  }
  /**
   * Deserialize the User.
   *
   * @param {Object} - user data.
   *
   * @return {User} - new instance of User.
   * @throws {Error} - If raw is not valid
   */


  static deserialize(raw) {
    const schema = {
      id: Joi.number().integer().required(),
      username: Joi.string().required(),
      email: Joi.string().email().required(),
      firstName: Joi.string().allow(null),
      lastName: Joi.string().allow(null),
      storesId: Joi.array().required(),
      config: Joi.object({
        freshchat: Joi.object({
          restoreId: Joi.string().allow(null)
        }).unknown(true)
      }).unknown(true),
      sonarAccess: Joi.boolean(),
      pieChartAccess: Joi.boolean(),
      serializedType: Joi.string()
    };
    const {
      error,
      value: {
        id,
        username,
        email,
        firstName,
        lastName,
        storesId,
        config,
        sonarAccess,
        pieChartAccess
      }
    } = Joi.validate(raw, schema);

    if (null != error) {
      throw new Error(error);
    }

    const user = new User(id, username, email, firstName, lastName, storesId, config);

    if (sonarAccess) {
      user.grantSonarAccess();
    }

    if (pieChartAccess) {
      user.grantPieChartAccess();
    }

    return user;
  }

}

exports.User = User;
